import React from 'react';
import SectionLayout from '@/components/SectionLayout';
import CTAButton from '@/components/CTAButton';
import { Target, Zap, Search, MessageSquare, Clock, Gift, TrendingUp, Globe, CheckCircle, FileText, Users, Mail, Mic } from 'lucide-react';
const CourseContentSection = () => {
  return <SectionLayout paddingY="py-20" backgroundColor="bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Simple Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-[#FEE6D8] text-[#003a70] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Gift className="w-4 h-4" />
            WHAT'S INSIDE THE COURSE
          </div>
          
          <h2 className="text-4xl font-bold text-[#003a70] mb-4">
            Inside the{' '}
            <span className="bg-[#FEE6D8] px-3 py-1 rounded-lg">"Local AI Assistant Launchpad,"</span>
          </h2>
          
          <h3 className="text-2xl font-semibold text-gray-700 mb-6">
            You'll Discover:
          </h3>
          
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Everything you need to start offering high-value AI assistant services to local businesses – with zero coding required and maximum impact guaranteed.
          </p>
        </div>

        {/* Course Modules */}
        <div className="space-y-4 mb-16">
          {/* Module 1 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#003a70] rounded-lg flex items-center justify-center flex-shrink-0">
                <Target className="text-white w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#003a70] mb-2">
                  The "FAQ Headache Eraser"
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Pinpoint the #1 time-wasting frustration for countless local businesses (and how you become their instant hero by solving it!).
                </p>
              </div>
            </div>
          </div>

          {/* Module 2 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#FEE6D8] border border-[#FEE6D8]/50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Zap className="text-[#003a70] w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#003a70] mb-2">
                  Our "AskSpot Quick-Build" Blueprint
                </h3>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Watch over my shoulder (I'm Dave Calafiore, AskSpot Co-founder) as I reveal precisely how to construct an effective AI Assistant for a local business in record time – <strong className="text-[#003a70]">NO CODING, NO COMPLEX TECH SKILLS NEEDED!</strong>
                </p>
                <p className="text-gray-600 text-sm italic">
                  If you can navigate a website, you can master this.
                </p>
              </div>
            </div>
          </div>

          {/* Module 3 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#003a70] rounded-lg flex items-center justify-center flex-shrink-0">
                <Search className="text-white w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#003a70] mb-2">
                  The "Smart & Simple" Prospecting Method
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Ditch the high-pressure tactics. We'll show you easy, respectful ways to identify businesses that will genuinely benefit from this solution. <span className="font-semibold text-[#003a70]">(Hint: Your existing network and some focused online observation are key!)</span>
                </p>
              </div>
            </div>
          </div>

          {/* Module 4 */}
          

          {/* Module 5 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#003a70] rounded-lg flex items-center justify-center flex-shrink-0">
                <Clock className="text-white w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#003a70] mb-2">
                  The 5-Minute "Problem-Solved" Demo That Resonates
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Learn how to showcase the AI Assistant by focusing entirely on their business's relief and benefits, making the value undeniable.
                </p>
              </div>
            </div>
          </div>

          {/* Module 6 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#FEE6D8] border border-[#FEE6D8]/50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Gift className="text-[#003a70] w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#003a70] mb-2">
                  The Irresistible "Easy Start" Offer
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Structure a simple, low-commitment, high-value package that makes saying "yes" the logical next step for business owners. <span className="font-semibold text-[#003a70]">(This is your ultimate "Trusted Advisor" entry point)</span>
                </p>
              </div>
            </div>
          </div>

          {/* Module 7 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#003a70] rounded-lg flex items-center justify-center flex-shrink-0">
                <TrendingUp className="text-white w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#003a70] mb-2">
                  From "AI Helper" to "Indispensable Partner"
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Uncover how to leverage the insights from this simple AI assistant to identify further client needs, leading to natural opportunities to offer your web design, SEO, and other marketing expertise. <span className="font-semibold text-[#003a70]">(Transform a simple service into a long-term, mutually beneficial client relationship!)</span>
                </p>
              </div>
            </div>
          </div>

          {/* Module 8 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#FEE6D8] border border-[#FEE6D8]/50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Globe className="text-[#003a70] w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#003a70] mb-2">
                  The "Website Essential" Strategy
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  If you build websites, this is a game-changer. Discover how to seamlessly integrate a "Local AI Assistant" as a standard, value-added feature for every new website project, creating an excellent recurring revenue stream with minimal extra effort.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Bonuses Section with Magic */}
        <div className="mb-16 relative">
          {/* Magical floating elements */}
          <div className="absolute -top-8 -left-8 w-16 h-16 bg-gradient-to-br from-[#FEE6D8]/30 to-[#003a70]/20 rounded-full blur-xl animate-pulse"></div>
          <div className="absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-br from-[#003a70]/30 to-[#FEE6D8]/20 rounded-full blur-lg animate-pulse" style={{
          animationDelay: '1s'
        }}></div>
          <div className="absolute -bottom-6 left-1/4 w-8 h-8 bg-gradient-to-br from-[#FEE6D8]/40 to-transparent rounded-full blur-lg animate-pulse" style={{
          animationDelay: '2s'
        }}></div>

          <div className="text-center mb-12 relative z-10">
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-[#FEE6D8] via-[#FEE6D8]/95 to-[#FEE6D8] text-[#003a70] px-8 py-4 rounded-full text-lg font-bold mb-6 shadow-2xl relative overflow-hidden">
              {/* Animated shimmer effect with proper CSS */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 translate-x-[-100%] animate-[shimmer_3s_infinite]"></div>
              <Gift className="w-6 h-6 relative z-10 animate-bounce" />
              <span className="relative z-10 text-base">PLUS, ALL THESE VALUABLE BONUS ASSETS:</span>
            </div>
          </div>

          {/* Enhanced bonus container with magical effects */}
          <div className="relative overflow-hidden">
            {/* Animated background gradients */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#003a70] via-[#003a70]/95 to-[#003a70]/90 rounded-3xl"></div>
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-[#FEE6D8]/10 to-transparent rounded-3xl animate-pulse"></div>
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#FEE6D8]/50 to-transparent"></div>
            
            {/* Floating sparkles */}
            <div className="absolute top-8 left-8 w-2 h-2 bg-[#FEE6D8] rounded-full opacity-60 animate-ping"></div>
            <div className="absolute top-16 right-12 w-1 h-1 bg-white rounded-full opacity-80 animate-ping" style={{
            animationDelay: '0.5s'
          }}></div>
            <div className="absolute bottom-16 left-16 w-1.5 h-1.5 bg-[#FEE6D8]/70 rounded-full opacity-60 animate-ping" style={{
            animationDelay: '1s'
          }}></div>
            <div className="absolute bottom-8 right-8 w-1 h-1 bg-white/60 rounded-full opacity-80 animate-ping" style={{
            animationDelay: '1.5s'
          }}></div>

            <div className="relative z-10 p-4 sm:p-6 md:p-8 lg:p-10">
              <div className="space-y-6 sm:space-y-8">
                {/* Bonus 1 */}
                <div className="flex items-start gap-4 sm:gap-6 group hover:transform hover:scale-105 transition-all duration-300">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-[#FEE6D8]/30 to-[#FEE6D8]/10 rounded-xl flex items-center justify-center flex-shrink-0 border border-[#FEE6D8]/40 shadow-lg group-hover:shadow-xl group-hover:bg-[#FEE6D8]/40 transition-all duration-300">
                    <FileText className="text-[#FEE6D8] w-6 h-6 sm:w-7 sm:h-7 group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <div className="group-hover:translate-x-2 transition-transform duration-300">
                    <h4 className="text-xl sm:text-2xl font-bold text-[#FEE6D8] mb-2 sm:mb-3 group-hover:text-white transition-colors duration-300">
                      Client Onboarding Questionnaire
                    </h4>
                    <p className="text-white/95 leading-relaxed text-base sm:text-lg group-hover:text-white transition-colors duration-300">
                      The "Quick Setup Guide" Google Doc Template to streamline your client discovery process and gather all the information you need to build their AI assistant efficiently.
                    </p>
                  </div>
                </div>

                {/* Bonus 2 */}
                <div className="flex items-start gap-4 sm:gap-6 group hover:transform hover:scale-105 transition-all duration-300">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-[#FEE6D8]/30 to-[#FEE6D8]/10 rounded-xl flex items-center justify-center flex-shrink-0 border border-[#FEE6D8]/40 shadow-lg group-hover:shadow-xl group-hover:bg-[#FEE6D8]/40 transition-all duration-300">
                    <Users className="text-[#FEE6D8] w-6 h-6 sm:w-7 sm:h-7 group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <div className="group-hover:translate-x-2 transition-transform duration-300">
                    <h4 className="text-xl sm:text-2xl font-bold text-[#FEE6D8] mb-2 sm:mb-3 group-hover:text-white transition-colors duration-300">&quot;Local AI Assistant&quot; Benefits One-Sheeter + Presentation</h4>
                    <p className="text-white/95 leading-relaxed text-base sm:text-lg group-hover:text-white transition-colors duration-300">
                      Customizable Canva Template that clearly explains the value proposition to prospects, making it easy to showcase the benefits of your AI assistant service.
                    </p>
                  </div>
                </div>

                {/* Bonus 3 */}
                <div className="flex items-start gap-4 sm:gap-6 group hover:transform hover:scale-105 transition-all duration-300">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-[#FEE6D8]/30 to-[#FEE6D8]/10 rounded-xl flex items-center justify-center flex-shrink-0 border border-[#FEE6D8]/40 shadow-lg group-hover:shadow-xl group-hover:bg-[#FEE6D8]/40 transition-all duration-300">
                    <Mail className="text-[#FEE6D8] w-6 h-6 sm:w-7 sm:h-7 group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <div className="group-hover:translate-x-2 transition-transform duration-300">
                    <h4 className="text-xl sm:text-2xl font-bold text-[#FEE6D8] mb-2 sm:mb-3 group-hover:text-white transition-colors duration-300">
                      Outreach Email/DM Templates
                    </h4>
                    <p className="text-white/95 leading-relaxed text-base sm:text-lg group-hover:text-white transition-colors duration-300">
                      Ready to Copy & Paste templates that take the guesswork out of initial outreach, helping you start conversations that convert into clients.
                    </p>
                  </div>
                </div>

                {/* Bonus 4 */}
                <div className="flex items-start gap-4 sm:gap-6 group hover:transform hover:scale-105 transition-all duration-300">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-[#FEE6D8]/30 to-[#FEE6D8]/10 rounded-xl flex items-center justify-center flex-shrink-0 border border-[#FEE6D8]/40 shadow-lg group-hover:shadow-xl group-hover:bg-[#FEE6D8]/40 transition-all duration-300">
                    <Mic className="text-[#FEE6D8] w-6 h-6 sm:w-7 sm:h-7 group-hover:scale-110 transition-transform duration-300" />
                  </div>
                  <div className="group-hover:translate-x-2 transition-transform duration-300">
                    <h4 className="text-xl sm:text-2xl font-bold text-[#FEE6D8] mb-2 sm:mb-3 group-hover:text-white transition-colors duration-300">The Niche &amp; Industry Specific FAQs Guide</h4>
                    <p className="text-white/95 leading-relaxed text-base sm:text-lg group-hover:text-white transition-colors duration-300">Curated lists of commonly asked questions and answers tailored to specific industries or professions, like dentistry. These FAQs can be used to train your client's chatbot, ensuring it provides accurate and relevant responses to inquiries typical of their business.</p>
                  </div>
                </div>

                {/* Additional Resources Note with enhanced styling */}
                <div className="border-t border-[#FEE6D8]/30 pt-6 sm:pt-8 mt-8 sm:mt-12 relative">
                  <div className="absolute left-1/2 top-0 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-gradient-to-br from-[#FEE6D8] to-[#FEE6D8]/70 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-[#003a70] rounded-full"></div>
                  </div>
                  <div className="text-center">
                    <p className="text-[#FEE6D8] text-lg sm:text-xl font-bold italic relative">
                      <span className="relative z-10 bg-gradient-to-r from-[#FEE6D8] to-white bg-clip-text text-transparent">
                        And more resources designed to accelerate your success!
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <div className="bg-[#003a70] rounded-2xl p-8 mb-8">
            <h3 className="text-3xl font-bold text-white mb-4">
              Ready to Transform Your Agency?
            </h3>
            <p className="text-[#FEE6D8] text-lg mb-6">
              Everything you need is waiting inside. Start building your AI assistant service today and watch your client relationships transform from transactions to trusted partnerships.
            </p>
            
            <CTAButton variant="primary" to="https://cart.deliciousmarketing.io/local-ai-assistant-launchpad/?coupon=AI-LAUNCHPAD" className="px-10 py-4 bg-[#FEE6D8] text-[#003a70] hover:bg-[#FEE6D8]/90 font-bold text-sm">
              Get Instant Access Now
            </CTAButton>
            
            <div className="mt-4 flex items-center justify-center gap-6 text-[#FEE6D8]/80 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                <span>Instant Access</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                <span>No Coding Required</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                <span>Start Today</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom keyframes for shimmer animation */}
      <style dangerouslySetInnerHTML={{
      __html: `
          @keyframes shimmer {
            0% { transform: translateX(-100%) skewX(-12deg); }
            100% { transform: translateX(200%) skewX(-12deg); }
          }
        `
    }} />
    </SectionLayout>;
};
export default CourseContentSection;